import {defineStore} from "pinia";
import {reactive, ref, computed} from "vue";
import {injectEnv} from "@fd/Environment.ts";
import {MenuGroup, type NavigateItem, normalizeTopMenu, type SideMenu, TopMenu, type MenuItem, ExecuteItem, SeparatorItem, Menu, ActionItem, MenuOpen, SideMenuChanges, MenuChange, findGroupPosition, findItemPosition, OnClickHandler} from "@t/ui/Menu.ts";
import type {History, HistoryObject} from "@t/meta/History.ts";
import type {Stacker, StackPage} from "@t/meta/Stacker.ts";
import {setStackerIndex} from "@/utils/Stacker.ts";
import {useRoute, type LocationQueryRaw} from "vue-router";
import {FormEnvironment} from "@fd/Types.ts";
import {ACTIVITY_REFS, addContextParams, AppContextMap, BUSCASE_REFS, FACT_REFS, OFFER_REFS, OPPORTUNITY_REFS, PROBLEM_REFS, toQueryString} from "@t/fd/AppContext.ts";
import {ContactType} from "@t/contact/Contact.ts";
import {OfferConcept} from "@t/offer/Offer.ts";
import {BusCaseType} from "@t/buscase/BusCase.ts";
import {ListItem, ListItemBase} from "@t/list/List.ts";
import {lazyLoad} from "@/store/Store.ts";
import {getHash, go, goParams, parseLocalUrl, removeParams, routeActiveSection, router, toFullPath, toSubContextPath} from "@/router/Router.ts";
import {escapeHtml} from "@t/util/SafeHtml.ts";
import {ActionPanelInstance} from "@t/ui/ActionPanel.ts";
import startWorkSheetAction from "@/action/StartWorkSheetAction.ts";
import {openNewMailWindow} from "@/intouch";
import {SharedContextDirect} from "@/chat/ChatClient.ts";

export interface OptionProvider {
	get: (name:string) => boolean;
	set: (name:string, value: boolean) => void;
}

function emptySideMenu():SideMenu {
	return { groups: [] };
}

function standardSideMenu(env:FormEnvironment):SideMenu|null {
	const sm: SideMenu = { groups: [] };
	const g:MenuGroup = {
		name:"menu_new",
		title: env.msg('menu.title.newRecord'),
		items:[]
	};
	g.items.push(<NavigateItem>{
		name: "createOrg",
		type: "nav",
		icon: "org",
		title: env.msg('menu.createOrg'),
		link: "createParty.do?t=ORGANIZATION"
	});
	g.items.push(<NavigateItem>{
		name: "createPerson",
		type: "nav",
		icon: "individual",
		title: env.msg('menu.createPerson'),
		link: "createParty.do?t=INDIVIDUAL"
	});
	sm.groups.push(g);
	return sm;
}


interface ListItemWitIcon extends ListItemBase {
	icon: string;
}

export type SideMenuProvider = SideMenu | (() => SideMenu);


export const useNavigationStore = defineStore('navigation', () => {
	const env = injectEnv();
	const route = useRoute();

	const activeSection = computed<string>(() => {
		return routeActiveSection(route);
	});

	// Menu collections
	const { data: offerConcepts} = lazyLoad<OfferConcept[]>(async () => {
		let ep = "api/offers/concepts";
		const resp = await env.net.fetch(ep);
		if (resp.ok) return await resp.json() as OfferConcept[];
		throw new Error(JSON.stringify(resp));
	}, []);

	const { data: busCaseTypes } = lazyLoad<BusCaseType[]>(async () => {
		let ep = "api/buscases/types";
		const resp = await env.net.fetch(ep);
		if (resp.ok) return await resp.json() as BusCaseType[];
		throw new Error(JSON.stringify(resp));
	}, []);

	const { data: problemTypes } = lazyLoad<ListItemWitIcon[]>(async () => {
		let ep = "api/problems/types";
		const resp = await env.net.fetch(ep);
		if (resp.ok) return await resp.json() as ListItemWitIcon[];
		throw new Error(JSON.stringify(resp));
	}, []);


	// TOP NAVIGATION
	const topMenu = ref<TopMenu>({
		sections: []
	});

	const chatContext = ref<SharedContextDirect|null>(null);

	function setTopMenu(data: TopMenu):void {
		data = normalizeTopMenu(data);
		topMenu.value.sections.length = 0;
		for (let sec of data.sections) topMenu.value.sections.push(sec);
	}

	async function loadTopMenu(): Promise<any> {
		let endpoint = "api/navigation/top";
		return env.net.fetch(endpoint)
			.then((resp) => {
				if (resp.ok) {
					resp.json().then(setTopMenu);
				}
			});
	}

	async function setOrLoadTopMenu(data?:TopMenu): Promise<any> {
		if (data) {
			setTopMenu(data);
			return Promise.resolve(data);
		}
		return loadTopMenu();
	}

	// SIDEBAR MENU
	const sideMenuProvider = ref<SideMenuProvider>({ groups:[] });
	const sideMenuChanges = ref<SideMenuChanges|null>(null);	// side menu modifications from the server
	const sideMenu = computed<SideMenu>(() => {
		let menu:SideMenu;
		if (typeof sideMenuProvider.value === "function") {
			menu = sideMenuProvider.value();
			// Dynamic menus are directly modified if there is a customization
			if (sideMenuChanges.value && sideMenuChanges.value.changes.length > 0) applyMenuChanges(menu, sideMenuChanges.value);
		} else {
			menu = sideMenuProvider.value;
			// Static menus have to be cloned to prevent modification of original data
			if (sideMenuChanges.value && sideMenuChanges.value.changes.length > 0) applyMenuChanges(structuredClone(menu), sideMenuChanges.value);
		}
		return menu;
	});
	const EXTENSION_MENU_GROUP:MenuGroup = { name:"menu_ext", open:"open", title: env.msg('menu.plugin'), items:[] };


	const showHistory = ref<boolean>(true);
	const historyLimit = ref<number>(15);
	const historyItems:HistoryObject[] = reactive<HistoryObject[]>([]);
	const actionPanels:ActionPanelInstance<any>[] = reactive<ActionPanelInstance<any>[]>([]);

	function loadSideMenuChanges(page: string, ctx?: AppContextMap) {
		let endpoint = env.net.urlParam("api/navigation/menu", "page", page);
		// Include action panels in the response
		endpoint = env.net.urlParam(endpoint, "panels", "true");
		if (ctx) {
			endpoint = addContextParams(endpoint, ctx);
		}
		env.net.fetch(endpoint)
			.then((resp) => {
				if (resp.ok) {
					if (resp.status === 200) {
						// There is a new version of side menu
						resp.json().then((chs:SideMenuChanges) => {
							if (Array.isArray(chs.panels)) {
								setActionPanels(chs.panels);
								delete chs.panels;
							}
							sideMenuChanges.value = chs
						});
					}
				}
			});
	}

	async function loadActionPanels():Promise<any> {
		let endpoint = "api/actionpanel";
		const resp = await env.net.fetch(endpoint);
		if (resp.ok) {
			setActionPanels(await resp.json() as ActionPanelInstance<any>[]);
		}
	}

	function applyMenuChanges(m:SideMenu, ch:SideMenuChanges):void {
		for (let change of ch.changes) {
			applyMenuChange(m, change);
		}
	}

	function targetMenuGroup(m:SideMenu, name:string):MenuGroup {
		let idx = findGroupPosition(m, name);
		if (idx < 0) idx = findGroupPosition(m, EXTENSION_MENU_GROUP.name);
		if (idx < 0) {
			m.groups.push({ ...EXTENSION_MENU_GROUP, items: []});
			idx = m.groups.length - 1;
		}
		return m.groups[idx];
	}

	function applyMenuChange(m:SideMenu, ch:MenuChange):void {
		switch (ch.op) {
			case "append":
				if (ch.item) {
					// ref is group name
					const mg:MenuGroup = targetMenuGroup(m, ch.ref);
					mg.items.push(ch.item);
				} else if (ch.group) {
					m.groups.push(ch.group);
				}
				break;
			case "prepend":
				if (ch.item) {
					// ref is group name
					const mg:MenuGroup = targetMenuGroup(m, ch.ref);
					mg.items.splice(0, 0, ch.item);
				} else if (ch.group) {
					m.groups.splice(0, 0, ch.group);
				}
				break;
			case "insert_before":
				if (ch.item) {
					const pos = findItemPosition(m, ch.ref);
					if (pos) {
						pos.list.splice(pos.index, 0, ch.item);
					} else {
						targetMenuGroup(m, EXTENSION_MENU_GROUP.name).items.push(ch.item);
					}
				} else if (ch.group) {
					let idx = findGroupPosition(m, ch.ref);
					m.groups.splice(Math.max(0, idx), 0, ch.group);
				}
				break;
			case "insert_after":
				if (ch.item) {
					const pos = findItemPosition(m, ch.ref);
					if (pos) {
						pos.list.splice(pos.index+1, 0, ch.item);
					} else {
						targetMenuGroup(m, EXTENSION_MENU_GROUP.name).items.push(ch.item);
					}
				} else if (ch.group) {
					let idx = findGroupPosition(m, ch.ref);
					if (idx >= 0) m.groups.splice(idx+1, 0, ch.group);
					else m.groups.push(ch.group);
				}
				break;
			case "remove":
				if (ch.remove === "item") {
					const pos = findItemPosition(m, ch.ref);
					if (pos) pos.list.splice(pos.index, 1);
				} else if (ch.remove === "group") {
					let idx = findGroupPosition(m, ch.ref);
					if (idx >= 0) m.groups.splice(idx, 1);
				}
				break;
		}
	}

	/** Install a new sidebar menu */
	function setSideMenu(menu:SideMenuProvider, options?:OptionProvider, history:boolean = true, page:string = null, ctx?:AppContextMap):void {
		sideMenuChanges.value = null;
		sideMenuProvider.value = menu;
		if (page) {
			loadSideMenuChanges(page, ctx); // ask backend to modify the menu
		} else {
			// Action panels are received in loadSideMenuChanges too
			loadActionPanels();
		}
		if (options) ops = options;
		else ops = null;
		showHistory.value = history;
	}

	/** Install default sidebar menu */
	function setStandardMenu(history:boolean = true):void {
		setSideMenu(standardSideMenu(env), null, history);
	}

	function setEmptyMenu(history:boolean = true):void {
		setSideMenu(emptySideMenu(), null, history);
	}

	/** You should call this on page unmount to prevent calling menu provider of an old component */
	function clearSideMenu():void {
		sideMenuChanges.value = null;
		sideMenuProvider.value = emptySideMenu();
	}

	let ops:OptionProvider = null;

	/**
	 * Sidebar can contain menu switches (options)
	 * Values of these options are accessed through these get/setOptions functions
	 */
	function getOption(name:string):boolean {
		if (ops) return ops.get(name);
		return false;
	}

	function setOption(name:string, value: boolean):void {
		if (ops) ops.set(name, value);
	}

	function setHistoryItems(items:HistoryObject[]):void {
		historyItems.length = 0;
		historyItems.push(...items);
	}

	function setActionPanels(panels:ActionPanelInstance<any>[]):void {
		actionPanels.length = 0;
		actionPanels.push(...panels);
	}

	/**
	 * Execute backend action for given panel
	 * @param panelId ID of the panel
	 * @param action name of the action
	 * @param params optional request parameters
	 * @param data optional payload (POST will be executed with this data)
	 */
	async function panelAction(panelId: string, action: string, params:Record<string, number|string|boolean> = {}, data: Record<string, any> = null): Promise<boolean> {
		let ep = "api/actionpanel/"+panelId;
		ep = env.net.urlParam(ep, "action", action);
		for (let k of Object.keys(params)) {
			ep = env.net.urlParam(ep, k, params[k]);
		}
		const resp = await env.net.fetch(ep, data == null ? "GET" : "POST", data);
		if (resp.ok) {
			const panels = await resp.json() as ActionPanelInstance<any>[];
			setActionPanels(panels);
			return true;
		}
		return false;
	}

	async function savePanelComment(panelId:string, comment:string):Promise<boolean> {
		return await panelAction(panelId, "comment", {}, { comment: comment });
	}

	const hasHistory = computed<boolean>(() => historyItems.length > 0);

	function reloadHistory() {
		env.net.fetch("api/history?limit="+historyLimit.value)
			.then((response) =>  response.json())
			.then((hist:History) => {
				setHistoryItems(hist.items);
			});
	}

	/** Insert a new item into history and reload current history list */
	function display(srcType:number, srcId:number|string, srcSubType?:number):void {
		let ep = "api/history/display?limit="+historyLimit.value;
		ep = env.net.urlParam(ep, "type", srcType);
		ep = env.net.urlParam(ep, "id", srcId);
		if (srcSubType) ep = env.net.urlParam(ep, "subType", srcSubType);
		env.net.fetch(ep)
			.then((response) =>  response.json())
			.then((hist:History) => {
				setHistoryItems(hist.items);
			});
	}

	function dirtyHistory():void {
		setTimeout(reloadHistory, 1000);
	}


	// STACKER
	const stacker:StackPage[] = reactive<StackPage[]>([]);
	const stackerLoaded = ref(false);

	function loadStacker(page?:StackPage, pop = -1, at:number = -1) {
		let endpoint = "api/stacker";
		if (pop > 0) endpoint = env.net.urlParam(endpoint, "pop", pop);
		else if (at >= 0) endpoint = env.net.urlParam(endpoint, "at", at);
		const connect = page ? env.net.fetch(endpoint, "POST", page) : env.net.fetch(endpoint);
		connect
			.then((response) =>  response.json())
			.then((st:Stacker) => {
				//console.dir(st);
				stacker.length = 0;
				let index = -1;
				for (let pg of st.pages) {
					stacker.push(pg);
					index++;
				}
				if (index >= 0) setStackerIndex(index);
				stackerLoaded.value = true;
				setPageTitle();
			});
	}

	function setPageTitle() {
		if (stacker.length > 0) {
			const name = stacker[stacker.length - 1].title;
			const template = Server.title;
			const title = template.replace("{title}", escapeHtml(name));
			const titleElement = document.querySelector("html >  head > title") as HTMLTitleElement;
			if (titleElement) {
				titleElement.innerText = title;
			}
		}
	}

	/**
	 * Construct StackPage from current route
	 */
	function currentStackPage(name:string):StackPage {
		const sp:StackPage = { title: name };
		if (route.path) {
			sp.path = route.path;
			if (Server.context && Server.context !== "/") {
				if (sp.path.startsWith(Server.context)) sp.path = sp.path.substring(Server.context.length);
			}
		}
		if (route.hash) sp.anchor = route.hash;
		const at = {};
		let hasAttrs = false;
		for (let k of Object.keys(route.query)) {
			if (k === "_sidx_") continue;		// this is special param for managing stacker (ignored here)
			at[k] = route.query[k];
			hasAttrs = true;
		}
		if (hasAttrs) sp.attributes = at;
		return sp;
	}

	/**
	 * Function similar to currentStackPage but this doesn't you information about
	 * current route from Vue Router but the info is passed to the function
	 */
	function prepareStackPage(name:string, path:string, params?:LocationQueryRaw):StackPage {
		const hash = getHash(path);
		const sp:StackPage = { title: name, path: toSubContextPath(removeParams(path)) };
		if (hash) sp.anchor = hash;
		const at = {};
		let hasAttrs = false;
		if (params) {
			for (let k of Object.keys(params)) {
				if (k === "_sidx_") continue;		// this is special param for managing stacker (ignored here)
				at[k] = params[k];
				hasAttrs = true;
			}
		}
		if (hasAttrs) sp.attributes = at;
		return sp;
	}

	function forward(name:string):void {
		const idx:number = stackerIndexFromRoute();
		loadStacker(currentStackPage(name), -1, idx);
	}

	function replace(name:string):void {
		const idx = stackerIndex();
		const page = currentStackPage(name);
		if (idx >= 0) loadStacker(page, -1, idx);
		else loadStacker(page, 1);
	}

	function back():void {
		router.back();
	}

	/** Return current stacker index from loaded stack, or -1 if no path was loaded yet */
	function stackerIndexFromPath():number {
		if (stackerLoaded.value && stacker.length > 0) {
			return stacker.length - 1;
		}
		return -1;
	}

	/** Return current stacker index from Vue Router route (query param), or -1 if no _sidx_ param is there */
	function stackerIndexFromRoute():number {
		return parseIndex(route.query._sidx_, -1);
	}

	/** Return current stacker index from window.location.href, or -1 if no _sidx_ param is there */
	function stackerIndexFromHref():number {
		const parsed = parseLocalUrl(window.location.href);
		return parseIndex(parsed.query._sidx_, -1);
	}

	/**
	 * Return index of current page in stacker.
	 * The index can be determined
	 * a) from length of loaded stack
	 * b) from _sidx_ param
	 */
	function stackerIndex():number {
		if (stackerLoaded.value && stacker.length > 0) {
			return stacker.length - 1;
		}
		return parseIndex(route.query._sidx_, -1);
	}

	function parseIndex(idx:string|string[]|null|undefined, defalt: number):number {
		if (idx == null || idx.length === 0) return defalt;
		if (Array.isArray(idx)) idx = idx[0];
		return parseInt(idx);
	}

	function replaceWith(path:string, params?:LocationQueryRaw, name?:string):void {
		let currentIndex = stackerIndex();
		if (!params) params = {};
		if (currentIndex >= 0) params["_sidx_"] = currentIndex;
		if (goParams(path, params, true) && name) {
			// We're staying on this page... so we have to update stacker
			const page = prepareStackPage(name, path, params);
			if (currentIndex >= 0) loadStacker(page, -1, currentIndex);
			else loadStacker(page, 1);
		}
	}

	function stay():void {
		if (stackerLoaded.value) return;
		loadStacker();
	}

	function addSideMenuGroup(name: string, title: string): void {
		if (typeof sideMenuProvider.value !== "function") {
			const g: MenuGroup = {
				name: name,
				title: title,
				items: []
			};
			sideMenuProvider.value.groups.push(g);
		}
	}

	function addSideMenuItem(groupName: string, item: MenuItem): boolean {
		console.warn("Using deprecated addSideMenuItem");
		if (typeof sideMenuProvider.value !== "function") {
			const found = sideMenuProvider.value.groups.find((group) => group.name === groupName);
			if (found) {
				found.items.push(item);
				return true;
			}
		}
		return false;
	}

	function addSideMenuItemIf(groupName: string, item: MenuItem, visible: boolean): boolean {
		if (visible) return addSideMenuItem(groupName, item);
		return false;
	}

	let menuUID = 0;

	function menuBuilder(ctx?:AppContextMap) {

		function navItem(id:string, url:string, titleRes:string, icon:string, addCtx:boolean|string[] = false):NavigateItem {
			if (addCtx) url = addContextParams(url, ctx, addCtx);
			return <NavigateItem>{
				name: id,
				type: "nav",
				icon: icon,
				title: env.msg(titleRes),
				link: url
			};
		}

		function executeItem(id:string, onclick:string|OnClickHandler, titleRes:string, icon:string):ExecuteItem {
			return <ExecuteItem>{
				name: id,
				type: "action",
				icon: icon,
				title: env.msg(titleRes),
				onclick: onclick
			};
		}

		function confirmDelete<T extends ActionItem>(menuItem:T, msgKey: string):T {
			menuItem.confirm = env.msg(msgKey);
			menuItem.confirmYes = env.msg('butt.delete');
			menuItem.confirmNo = env.msg('butt.cancel');
			menuItem.confirmIcon = "trash";
			menuItem.confirmIconColor = "#EE3300";
			return menuItem;
		}

		function separator(uid?:string):SeparatorItem {
			if (uid == null) uid = "sep-"+(menuUID++);
			return { name: uid, type: "separator" };
		}

		function menu(id:string, titleRes:string, icon:string):Menu {
			return { name: id, type: "menu", icon:icon, title: env.msg(titleRes), children:[] };
		}

		function menuGroup(id:string, titleRes:string, open: MenuOpen = "open"):MenuGroup {
			return { name:id, title: env.msg(titleRes), open, items:[] };
		}

		function newRecordGroup(srcType:number, srcId:number|string, options = {
			mailMembers: false,
			facts: false,
			party: true
		}) {
			const g = menuGroup("menu_new", 'menu.title.newRecord');
			if (Server.mod.communication) g.items.push(navItem("createContact", "createContact.do", "menu.createContact", "call", ACTIVITY_REFS));
			g.items.push(navItem("planEvent", "planEvent.do", "menu.planEvent", "event", ACTIVITY_REFS));
			g.items.push(navItem("createTask", "createTask.do", "menu.createTask", "task", ACTIVITY_REFS));
			if (Server.mod.email && Server.hasMail) {
				g.items.push(executeItem("newMail", () => { openNewMailWindow(toQueryString(ctx, ACTIVITY_REFS)) }, "menu.composeMail", "send"));
				if (options.mailMembers) g.items.push(executeItem("newMailMembers", () => { openNewMailWindow(toQueryString(ctx, ACTIVITY_REFS)+"&includeMembers=true&root="+srcId) }, "menu.composeMailMembers", "bulk-email"));
			}
			if (Server.mod.sms) g.items.push(navItem("newSMS", "createSMS.do", "menu.createSMS", "mobile-messages", ACTIVITY_REFS));
			if (Server.mod.communication) g.items.push(navItem("createLetter", "createContact.do?type="+ContactType.LETTER, "menu.composeLetter", "mail", ACTIVITY_REFS));
			g.items.push(separator("sep-new-1"));
			if (Server.mod.opportunity) {
				g.items.push(navItem("createOpportunity", "createOpportunity.do", "menu.createOpportunity", "opportunity", OPPORTUNITY_REFS));
			}
			if (Server.mod.offer) {
				const m = menu("createOffer", "menu.createOffer", "offer");
				m.children.push(navItem("createOffer0", "createOffer.do", "offer.noTemplate", "offer", OFFER_REFS));
				for (let ot of offerConcepts.value) {
					m.children.push(navItem("createOffer"+ot.id, "createOffer.do?offer="+ot.id, ot.name, "offer-template", OFFER_REFS));
				}
				g.items.push(m);
			}
			if (Server.mod.buscase) {
				const m = menu("createBusCase", "menu.createBusCase", "case");
				for (let bct of busCaseTypes.value) {
					m.children.push(navItem("createBusCase"+bct.id, "createBusCase.do?bctype="+bct.id, bct.localizedName, bct.icon, BUSCASE_REFS))
				}
				g.items.push(m);
			}
			if (Server.mod.helpdesk) {
				const m = menu("createProblem", "menu.createProblem", "flash");
				m.children.push(navItem("createProblem", "createProblem.do?problemType=6req", "menu.createProblem", "flash", PROBLEM_REFS));
				for (let pt of problemTypes.value) {
					m.children.push(navItem("createProblem_"+pt.value, "createProblem.do?problemType="+pt.value, pt.name, pt.icon, PROBLEM_REFS))
				}
				g.items.push(m);
			}
			if (options.party) {
				g.items.push(separator("sep-new-2"));
				g.items.push(navItem("createOrg", "createParty.do?t=ORGANIZATION", "menu.createOrg", "org"));
				g.items.push(navItem("createPerson", "createParty.do?t=INDIVIDUAL", "menu.createPerson", "individual"));
			}
			g.items.push(separator("sep-new-3"));
			if (Server.mod.comment) g.items.push(navItem("createComment", "createComment.do?srcType="+srcType+"&srcId="+srcId, "menu.createComment", "add-comment"));
			if (Server.mod.worksheet) {
				g.items.push(navItem("createWorkSheet", "createWorkSheet.do?srcType="+srcType+"&srcId="+srcId, "menu.createWorkSheet", "worksheet"));
				g.items.push(executeItem("startWorkSheet", (env) => { startWorkSheetAction(env, srcType, srcId) }, "menu.startWorkSheet", "timer"));
			}
			if (options.facts) g.items.push(navItem("createFact", "createFact.do", "menu.createFact", "fact", FACT_REFS));
			return g;
		}

		return { navItem, menuGroup, newRecordGroup, confirmDelete };
	}

	return {
		topMenu, loadTopMenu, setTopMenu, setOrLoadTopMenu,
		sideMenu, setSideMenu, setStandardMenu, setEmptyMenu, clearSideMenu, getOption, setOption,

		showHistory, historyItems, reloadHistory, dirtyHistory, setHistoryItems, display, historyLimit,
		stacker, stackerLoaded, loadStacker, forward, replace, replaceWith, stay, stackerIndex,
		addSideMenuGroup, addSideMenuItem, addSideMenuItemIf,

		setActionPanels, actionPanels, panelAction, savePanelComment, loadActionPanels,

		activeSection, hasHistory,

		offerConcepts, busCaseTypes, problemTypes,

		menuBuilder,
		/** Use to provide a context to the chat client window */
		chatContext,

		back
	};
});